import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, Optional, Self, Input, HostBinding, HostListener, NgModule } from '@angular/core';
import { InputDirective } from '@eui/components/shared';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/forms';
import { CommonModule } from '@angular/common';

class EuiInputRadioComponent extends InputDirective {
    constructor(ngControl, viewContainerRef, _elementRef, _renderer, _cd, injector) {
        super(_elementRef, _renderer, ngControl, injector);
        this.ngControl = ngControl;
        this.viewContainerRef = viewContainerRef;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._cd = _cd;
        this.type = 'radio';
        this.onChange = (_) => { };
        this.onTouched = (_) => { };
        // Firefox fix: set type to radio before first ngDoCheck runs
        this._elementRef.nativeElement.type = 'radio';
        // if there's no id attribute set one
        if (!this._id) {
            this.setIdAttribute();
        }
        // register control valueAccessor
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }
    get isInvalid() {
        return this._isInvalid || null;
    }
    set isInvalid(state) {
        this.setInvalid(state);
    }
    set class(cssClass) {
        super.styleClass = cssClass;
    }
    get class() {
        return [
            super.getCssClasses('eui-input-radio'),
            this._isInvalid ? 'eui-input-radio--invalid' : '',
        ].join(' ').trim();
    }
    get defaultChecked() {
        return this._defaultChecked;
    }
    set defaultChecked(value) {
        this._defaultChecked = coerceBooleanProperty(value);
    }
    get selected() {
        return this._elementRef?.nativeElement.checked;
    }
    get value() {
        return this._value;
    }
    set value(value) {
        if (typeof value === 'boolean' || !!value) {
            this._value = value;
            this._elementRef.nativeElement.value = this._value;
        }
    }
    ngOnInit() {
        super.ngOnInit();
        // in case control value is null set the default one (isChecked) and sync Control State
        // if (this.ngControl?.control?.value === null) {
        // this.ngControl.control.setValue('', { emitModelToViewChange: false });
        // changing Model Expression after view checked, so detect changes
        // TODO: check why although it's checked .checked returns false
        // this.ngControl.viewToModelUpdate(this._checked);
        // this._cd.detectChanges();
        // }
        if (this.ngControl) {
            this.ngControl.statusChanges.subscribe((status) => {
                this.isInvalid = status === 'INVALID';
                this.euiDanger = this.isInvalid;
            });
        }
    }
    ngDoCheck() {
        if (this.ngControl) {
            this.isInvalid = this.ngControl.invalid && this.ngControl.touched;
        }
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        // when readonly changes hide other radio (input+label)
        if (changes['readonly']) {
            // in readonly hide all input elements.
            if (this.readonly) {
                this._renderer.addClass(this._elementRef.nativeElement, 'eui-input-radio--hide');
            }
            else {
                this._renderer.removeClass(this._elementRef.nativeElement, 'eui-input-radio--hide');
            }
            this.processReadonly();
        }
        if (changes['isInvalid']) {
            if (changes['isInvalid'].currentValue) {
                this._renderer.addClass(this._elementRef.nativeElement, 'eui-input-radio--invalid');
            }
            else {
                this._renderer.removeClass(this._elementRef.nativeElement, 'eui-input-radio--invalid');
            }
        }
    }
    writeValue(obj) {
        // set checked state based if radio value matches the control's one
        this._elementRef.nativeElement.checked = (this._elementRef.nativeElement.value === obj);
        this.processReadonly();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onCheckedChanged(target) {
        this._defaultChecked = target.checked;
        this.onChange(target.value);
    }
    setInvalid(state) {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);
        // set BaseDirective Attribute
        super.euiDanger = this._isInvalid;
    }
    processReadonly() {
        const notRender = this._readonly && !this._elementRef.nativeElement.checked;
        // control the label that will be rendered
        const labelElementRef = document.querySelector(`[for=${this._id}]`);
        if (labelElementRef) {
            if (notRender) {
                this._renderer.setStyle(labelElementRef, 'display', 'none');
            }
            else {
                this._renderer.removeStyle(labelElementRef, 'display');
            }
        }
    }
}
/** @nocollapse */ EuiInputRadioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i0.ViewContainerRef }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiInputRadioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiInputRadioComponent, selector: "input[euiInputRadio]", inputs: { isInvalid: "isInvalid", class: "class", defaultChecked: ["checked", "defaultChecked"], value: "value" }, host: { listeners: { "change": "onCheckedChanged($event.target)" }, properties: { "class": "this.class", "attr.type": "this.type", "attr.checked": "this.defaultChecked" } }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: '', isInline: true, styles: [".eui-input-radio:not(.cdk-visually-hidden){-webkit-appearance:inherit;appearance:inherit;align-items:center;border-radius:50%;cursor:pointer;display:inline-flex;flex-shrink:0;height:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-2xs));margin-right:var(--eui-base-spacing-s);position:relative;vertical-align:bottom;transition:all .25s ease;width:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-2xs));accent-color:var(--eui-base-color-white);background:var(--eui-base-color-white);border:2px solid var(--eui-base-color-grey-75)}.eui-input-radio:not(.cdk-visually-hidden)+label,.eui-input-radio:not(.cdk-visually-hidden)+.eui-label{display:inline-flex;cursor:pointer;margin-right:var(--eui-base-spacing-m);white-space:inherit}.eui-input-radio:not(.cdk-visually-hidden):checked{background:var(--eui-base-color-white)}.eui-input-radio:not(.cdk-visually-hidden):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly]){background:var(--eui-base-color-grey-15);opacity:.25;pointer-events:none;-webkit-user-select:none;user-select:none}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly]):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly])+label{pointer-events:none}.eui-input-radio:not(.cdk-visually-hidden)[readonly]{display:none;opacity:.5;pointer-events:none;-webkit-user-select:none;user-select:none}.eui-input-radio:not(.cdk-visually-hidden)[readonly]:checked{display:inline-flex}.eui-input-radio:not(.cdk-visually-hidden)[readonly]:checked:before{background:var(--eui-base-color-grey-25);border-radius:50%;height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--invalid:not(:disabled):not([readonly]),.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--danger:not(:disabled):not([readonly]){border:2px solid var(--eui-base-color-danger-100)}.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--invalid:not(:disabled):not([readonly]):checked:before,.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--danger:not(:disabled):not([readonly]):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23da2131' stroke='%23da2131' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):active,.eui-input-radio:not(.cdk-visually-hidden):focus{outline:2px solid var(--eui-base-color-accessible-focus);outline-offset:0;-moz-outline-radius:var(--eui-base-border-radius);transition:outline .15s ease-in-out,border .15s ease-in-out}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'input[euiInputRadio]', template: '', encapsulation: ViewEncapsulation.None, styles: [".eui-input-radio:not(.cdk-visually-hidden){-webkit-appearance:inherit;appearance:inherit;align-items:center;border-radius:50%;cursor:pointer;display:inline-flex;flex-shrink:0;height:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-2xs));margin-right:var(--eui-base-spacing-s);position:relative;vertical-align:bottom;transition:all .25s ease;width:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-2xs));accent-color:var(--eui-base-color-white);background:var(--eui-base-color-white);border:2px solid var(--eui-base-color-grey-75)}.eui-input-radio:not(.cdk-visually-hidden)+label,.eui-input-radio:not(.cdk-visually-hidden)+.eui-label{display:inline-flex;cursor:pointer;margin-right:var(--eui-base-spacing-m);white-space:inherit}.eui-input-radio:not(.cdk-visually-hidden):checked{background:var(--eui-base-color-white)}.eui-input-radio:not(.cdk-visually-hidden):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly]){background:var(--eui-base-color-grey-15);opacity:.25;pointer-events:none;-webkit-user-select:none;user-select:none}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly]):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):disabled:not([readonly])+label{pointer-events:none}.eui-input-radio:not(.cdk-visually-hidden)[readonly]{display:none;opacity:.5;pointer-events:none;-webkit-user-select:none;user-select:none}.eui-input-radio:not(.cdk-visually-hidden)[readonly]:checked{display:inline-flex}.eui-input-radio:not(.cdk-visually-hidden)[readonly]:checked:before{background:var(--eui-base-color-grey-25);border-radius:50%;height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23004494' stroke='%23004494' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--invalid:not(:disabled):not([readonly]),.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--danger:not(:disabled):not([readonly]){border:2px solid var(--eui-base-color-danger-100)}.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--invalid:not(:disabled):not([readonly]):checked:before,.eui-input-radio:not(.cdk-visually-hidden).eui-input-radio--danger:not(:disabled):not([readonly]):checked:before{height:1rem;width:1rem;content:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='ionicon' viewBox='0 0 512 512'%3E%3Ctitle%3EEllipse%3C/title%3E%3Cpath fill='%23da2131' stroke='%23da2131' d='M256 464c-114.69 0-208-93.31-208-208S141.31 48 256 48s208 93.31 208 208-93.31 208-208 208z'/%3E%3C/svg%3E\")}.eui-input-radio:not(.cdk-visually-hidden):active,.eui-input-radio:not(.cdk-visually-hidden):focus{outline:2px solid var(--eui-base-color-accessible-focus);outline-offset:0;-moz-outline-radius:var(--eui-base-border-radius);transition:outline .15s ease-in-out,border .15s ease-in-out}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i0.ViewContainerRef }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i0.Injector }]; }, propDecorators: { isInvalid: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }, {
                type: Input
            }], type: [{
                type: HostBinding,
                args: ['attr.type']
            }], defaultChecked: [{
                type: HostBinding,
                args: ['attr.checked']
            }, {
                type: Input,
                args: ['checked']
            }], value: [{
                type: Input
            }], onCheckedChanged: [{
                type: HostListener,
                args: ['change', ['$event.target']]
            }] } });

class EuiInputRadioModule {
}
/** @nocollapse */ EuiInputRadioModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiInputRadioModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioModule, declarations: [EuiInputRadioComponent], imports: [CommonModule], exports: [EuiInputRadioComponent] });
/** @nocollapse */ EuiInputRadioModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiInputRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        EuiInputRadioComponent,
                    ],
                    exports: [
                        EuiInputRadioComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiInputRadioComponent, EuiInputRadioModule };
